import { Component, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiUrls } from 'src/app/core/classes/ApiUrls';
import { Helper } from 'src/app/shared/classes/Helper';
import { HitApi } from 'src/app/shared/classes/HitApi';
import { Widget } from 'src/app/shared/classes/Widget';
import { WidgetInjectedData } from 'src/app/shared/classes/WidgetInjectedData';
import { AuthorizationType } from 'src/app/shared/enums/AuthorizationType';
import { ButtonColorType } from 'src/app/shared/enums/ButtonColorType';
import { FilterStoreKey } from 'src/app/shared/enums/FilterStoreKey';
import { IconType } from 'src/app/shared/enums/IconType';
import { ModalAction } from 'src/app/shared/enums/ModalAction';
import { ModalType } from 'src/app/shared/enums/ModalType';
import { RequestType } from 'src/app/shared/enums/RequestType';
import { ICardsGeneratorInput } from 'src/app/shared/interfaces/card-generator/ICardsGeneratorInput';
import { IHitApi } from 'src/app/shared/interfaces/hit-api/IHitApi';
import { IModalData } from 'src/app/shared/interfaces/modal/IModalData';
import { ModalService } from 'src/app/shared/services/modal/modal-service/modal.service';
import { MultiStepFormService } from 'src/app/shared/services/modal/multi-step-form/multi-step-form.service';
import { IIcon } from '../../../../interfaces/icon-data/IIcon';
import { CreateAlertFirstStepComponent } from '../../../modal-templates/alert-listing-modal/create-alert-first-step/create-alert-first-step.component';
import { CreateAlertSecondStepComponent } from '../../../modal-templates/alert-listing-modal/create-alert-second-step/create-alert-second-step.component';
import { CreateAlertThirdStepComponent } from '../../../modal-templates/alert-listing-modal/create-alert-third-step/create-alert-third-step.component';
import { ButtonType } from './../../../../enums/ButtonType';
import { IButtonGeneratorInput } from './../../../../interfaces/button-generator/IButtonGeneratorInput';
import { NotificationsService } from './../../../../services/notifications/notifications.service';

@Component({
    selector: 'app-alert-listing',
    templateUrl: './alert-listing.component.html',
    styleUrls: ['./alert-listing.component.sass']
})
export class AlertListingComponent implements OnInit {
    cardInput: ICardsGeneratorInput;
    widgetRef: Widget;
    constructor(
        widgetData: WidgetInjectedData,
        private modalService: ModalService,
        private multiStepFormService: MultiStepFormService,
        private notificationService: NotificationsService
    ) {
        this.widgetRef = widgetData.widgetRef;
    }

    ngOnInit(): void {
        this.cardInput = {
            widgetIconData: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-exclamation-triangle'
            },
            cardTitle: {
                infoTitle: 'Alert Name',
                infoKey: 'alertName'
            },
            bulkActions: [
                {
                    buttonName: 'Delete',
                    function: (
                        buttonData: IButtonGeneratorInput,
                        selectedCards,
                        bulkLoader: BehaviorSubject<boolean>
                    ) => {
                        if (buttonData.disable) {
                            this.openFullAccessModal();
                            return;
                        }
                        this.modalService.openConfirmationModal({
                            modalName: 'Delete Alerts',
                            modalIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-trash'
                            },
                            contextIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-exclaimation-triangle'
                            },
                            confirmationMessage:
                                'Are you sure you want to delete selected alerts ?',
                            buttonColorType: ButtonColorType.WARN,
                            buttonText: 'Delete',
                            loaderOnExec: true,
                            function: (modalId: Symbol) => {
                                bulkLoader.next(true);
                                // Bulk Delete
                                const batchDeleteInput = {
                                    ids: selectedCards.map(
                                        (selCard) => selCard.id
                                    )
                                };
                                const apiArgs: IHitApi = {
                                    url: `${ApiUrls.ALERT_ENDPOINT}/batch`,
                                    intactUrl: `${ApiUrls.ALERT_ENDPOINT}/{batch}`,
                                    input: batchDeleteInput,
                                    uniqueIdentity: Symbol(),
                                    requestType: RequestType.DELETE,
                                    config: {
                                        authorization:
                                            AuthorizationType.BEARER_TOKEN
                                    },
                                    function: (response) => {
                                        this.widgetRef.notificationsService.showSnackBar(
                                            'Alerts deleted successfully'
                                        );
                                        bulkLoader.next(false);
                                        this.widgetRef.refreshWidget();
                                    },
                                    errorFunction: (error) => {
                                        bulkLoader.next(false);
                                        this.widgetRef.notificationsService.showSnackBar(
                                            error &&
                                                error.error &&
                                                error.error.message
                                                ? error.error.message
                                                : 'Error deleting alerts',
                                            true
                                        );
                                        this.widgetRef.refreshWidget();
                                    }
                                };
                                this.widgetRef.modalService.closeModal(
                                    null,
                                    modalId
                                );
                                new HitApi(
                                    apiArgs,
                                    this.widgetRef.httpService,
                                    this.widgetRef.ngZone
                                ).hitApi();
                            }
                        });
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-trash',
                        extraClass: 'color-warn'
                    },
                    buttonType: 'PRIMARY',
                    hoverText: 'Delete Selected Alerts',
                    showLoader: false,
                    disable: this.widgetRef.lightState
                },
                {
                    buttonName: 'Enable',
                    function: (
                        buttonData: IButtonGeneratorInput,
                        selectedCards,
                        bulkLoader: BehaviorSubject<boolean>
                    ) => {
                        if (buttonData.disable) {
                            this.openFullAccessModal();
                            return;
                        }
                        // Bulk Enable
                        bulkLoader.next(true);
                        const batchEnableInput = {
                            ids: selectedCards.map((selCard) => selCard.id)
                        };
                        const apiArgs: IHitApi = {
                            url: `${ApiUrls.ALERT_ENDPOINT}/batchEnable`,
                            intactUrl: `${ApiUrls.ALERT_ENDPOINT}/{batchEnable}`,
                            input: batchEnableInput,
                            uniqueIdentity: Symbol(),
                            requestType: RequestType.PATCH,
                            config: {
                                authorization: AuthorizationType.BEARER_TOKEN
                            },
                            function: (response) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Alerts enabed successfully'
                                );
                                bulkLoader.next(false);
                                this.widgetRef.refreshWidget();
                            },
                            errorFunction: (error) => {
                                bulkLoader.next(false);
                                this.widgetRef.notificationsService.showSnackBar(
                                    error && error.error && error.error.message
                                        ? error.error.message
                                        : 'Error enabling alerts',
                                    true
                                );
                                this.widgetRef.refreshWidget();
                            }
                        };
                        new HitApi(
                            apiArgs,
                            this.widgetRef.httpService,
                            this.widgetRef.ngZone
                        ).hitApi();
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-power-off',
                        extraClass: 'color-success'
                    },
                    buttonType: 'PRIMARY',
                    hoverText: 'Enable Selected Alerts',
                    showLoader: false,
                    disable: this.widgetRef.lightState
                },
                {
                    buttonName: 'Disable',
                    function: (
                        buttonData: IButtonGeneratorInput,
                        selectedCards,
                        bulkLoader: BehaviorSubject<boolean>
                    ) => {
                        if (buttonData.disable) {
                            this.openFullAccessModal();
                            return;
                        }
                        // Bulk Disable
                        bulkLoader.next(true);
                        const batchDeleteInput = {
                            ids: selectedCards.map((selCard) => selCard.id)
                        };
                        const apiArgs: IHitApi = {
                            url: `${ApiUrls.ALERT_ENDPOINT}/batchDisable`,
                            intactUrl: `${ApiUrls.ALERT_ENDPOINT}/{batchDisable}`,
                            input: batchDeleteInput,
                            uniqueIdentity: Symbol(),
                            requestType: RequestType.PATCH,
                            config: {
                                authorization: AuthorizationType.BEARER_TOKEN
                            },
                            function: (response) => {
                                bulkLoader.next(false);
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Alerts disabled successfully'
                                );
                                this.widgetRef.refreshWidget();
                            },
                            errorFunction: (error) => {
                                bulkLoader.next(false);
                                this.widgetRef.notificationsService.showSnackBar(
                                    error && error.error && error.error.message
                                        ? error.error.message
                                        : 'Error disabling alerts',
                                    true
                                );
                                this.widgetRef.refreshWidget();
                            }
                        };
                        new HitApi(
                            apiArgs,
                            this.widgetRef.httpService,
                            this.widgetRef.ngZone
                        ).hitApi();
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-power-off',
                        extraClass: 'color-warn'
                    },
                    buttonType: 'PRIMARY',
                    hoverText: 'Enable Selected Alerts',
                    showLoader: false,
                    disable: this.widgetRef.lightState
                }
            ],
            infoData: [
                {
                    infoTitle: 'Created By',
                    infoKey: 'createdBy|email'
                }
            ],
            buttons: [
                {
                    buttonName: 'New Alert',
                    buttonType: ButtonType.ICON,
                    buttonColorType: ButtonColorType.PRIMARY,
                    hoverText: 'New Alert',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-plus'
                    },
                    function: (buttonData: IButtonGeneratorInput) => {
                        if (buttonData.disable) {
                            this.openFullAccessModal();
                            return;
                        }
                        this.create();
                    },
                    disable: this.widgetRef.lightState
                }
            ],
            cardButtons: [
                {
                    buttonName: null,
                    buttonType: ButtonType.ICON,
                    buttonColorType: ButtonColorType.SECONDARY,
                    hoverText: 'Edit Alert',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-edit'
                    },
                    function: (buttonRef: IButtonGeneratorInput, cardData) => {
                        this.update(cardData);
                    }
                },
                {
                    buttonName: null,
                    buttonType: ButtonType.ICON,
                    buttonColorType: ButtonColorType.WARN,
                    hoverText: 'Delete Alert',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-trash'
                    },
                    function: (buttonRef: IButtonGeneratorInput, cardData) => {
                        this.delete(cardData);
                    }
                }
            ],
            titleActions: [
                {
                    buttonName: '',
                    showLoader: true,
                    function: (buttonRef: IButtonGeneratorInput, cardData) => {
                        this.toggleScheduler(cardData);
                    },
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-power-off'
                    },
                    ngStyleRenderer: (cardData) => {
                        return { color: cardData.active ? 'green' : 'red' };
                    },
                    hoverTextRenderer: (cardData) => {
                        return cardData.active
                            ? 'Disable Alert'
                            : 'Enable Alert';
                    }
                }
            ],
            listExtraction: {
                type: 'DIRECT'
            },
            selection: 'multiple',
            disableSelectAll: this.widgetRef.lightState,
            afterResponse: (response: any) => {
                if (this.widgetRef.lightState) {
                    if (response && response.length >= 1) {
                        this.cardInput.buttons.forEach(
                            (button) => (button.disable = true)
                        );
                    } else {
                        this.cardInput.buttons.forEach(
                            (button) => (button.disable = false)
                        );
                    }
                }
            }
        };
    }

    openFullAccessModal() {
        Helper.getFullAccess(
            this.widgetRef.modalService,
            this.widgetRef.widgetData.widgetInfo.portlet.type
        );
    }

    create() {
        const modalInfo = {
            label: 'Create Alert',
            purpose: ModalAction.CREATE
        };
        this.setModalDataAndOpen(modalInfo, null);
    }

    update(item) {
        const modalInfo = {
            label: 'Edit Alert',
            purpose: ModalAction.UPDATE
        };
        this.setModalDataAndOpen(modalInfo, item);
    }

    setModalDataAndOpen(modalInfo, stepData) {
        this.widgetRef.filterService.filtersDataTemp.delete(
            FilterStoreKey.ALERT_FILTERS
        );
        this.widgetRef.filterService.filtersData.delete(
            FilterStoreKey.ALERT_FILTERS
        );
        const modalData: IModalData = {
            modalName: modalInfo.label,
            modalHeightVh: 98,
            modalWidthVw: 85,
            modalIcon: {
                class: 'fas fa-exclamation-triangle',
                type: IconType.FONTAWSOME
            } as IIcon,
            sourceId: this.widgetRef.widgetData.widgetUniqueIdentifier,
            modalType: ModalType.MIDDLE,
            modalPurpose: modalInfo.purpose,
            modalSteps: [
                {
                    stepData: {
                        componentToLoad: CreateAlertFirstStepComponent,
                        payload: {
                            data: {
                                widgetRef: this.widgetRef,
                                stepData,
                                edit: stepData ? true : false
                            }
                        }
                    },
                    stepName: 'Select Moldules'
                },
                {
                    stepData: {
                        componentToLoad: CreateAlertSecondStepComponent,
                        payload: {
                            data: {
                                widgetRef: this.widgetRef,
                                stepData,
                                edit: stepData ? true : false
                            }
                        }
                    },
                    stepName: 'Select Filters'
                },
                {
                    stepData: {
                        componentToLoad: CreateAlertThirdStepComponent,
                        payload: {
                            data: {
                                widgetRef: this.widgetRef,
                                stepData,
                                edit: stepData ? true : false
                            }
                        }
                    },
                    stepName: 'Configure Notification'
                }
            ]
        };
        this.modalService.openModal(modalData);
    }

    delete(item) {
        this.modalService.openConfirmationModal({
            modalName: 'Delete Alert',
            modalIcon: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-trash'
            },
            contextIcon: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-exclaimation-triangle'
            },
            confirmationMessage: 'Are you sure you want to delete alert ?',
            buttonColorType: ButtonColorType.WARN,
            buttonText: 'Delete',
            loaderOnExec: true,
            function: (modalId: Symbol) => {
                this.deleteApi.call(this, item, modalId);
            }
        });
    }
    deleteApi(alert, modalId: Symbol) {
        const widgetRef: Widget = this.widgetRef;
        const apiArgs: IHitApi = {
            url: `${ApiUrls.ALERT_ENDPOINT}/${alert.id}`,
            intactUrl: `${ApiUrls.ALERT_ENDPOINT}/{alert-id}`,
            requestType: RequestType.DELETE,
            input: {},
            function: (response) => {
                this.widgetRef.refreshWidget();
                this.notificationService.showSnackBar(
                    'Alert deleted successfully'
                );
                this.modalService.closeModal(null, modalId);
            },
            errorFunction: (error) => {
                this.notificationService.showSnackBar(
                    error && error.error
                        ? error.error.message
                        : 'Error while deleting alert',
                    true
                );
                this.modalService.closeModal(null, modalId);
            },
            uniqueIdentity: Symbol(),
            config: {
                authorization: AuthorizationType.BEARER_TOKEN,
                ignoreBaseUrl: false
            }
        };
        new HitApi(apiArgs, widgetRef.httpService, widgetRef.ngZone).hitApi();
    }

    toggleScheduler(item: any): void {
        let status = '';
        if (item.active) {
            status = 'disable';
        } else {
            status = 'enable';
        }
        const widgetRef: Widget = this.widgetRef;
        const apiArgs: IHitApi = {
            url: `${ApiUrls.ALERT_ENDPOINT}/${status}/${item.id}`,
            intactUrl: `${ApiUrls.ALERT_ENDPOINT}/${status}/{alert-id}`,
            requestType: RequestType.PATCH,
            input: {},
            function: (response) => {
                item.active = !item.active;
            },
            errorFunction: (error) => {
                this.widgetRef.notificationsService.showSnackBar(
                    error && error.error && error.error.message
                        ? error.error.message
                        : `Error ${
                              item.active ? 'disabling' : 'enabling'
                          } alert`
                );
                this.widgetRef.refreshWidget();
            },
            uniqueIdentity: Symbol(),
            config: {
                authorization: AuthorizationType.BEARER_TOKEN,
                ignoreBaseUrl: false
            }
        };
        new HitApi(apiArgs, widgetRef.httpService, widgetRef.ngZone).hitApi();
    }
}
